import { setTrackedChangeContext } from 'common/Prose/Plugins/TrackChanges/utils'
import { findTableFromPos, findTableFromSel, isTableSelection } from 'common/Prose/utils/table'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { selectedRect } from 'common/Prose/utils/table/selectionRect'
import { schema } from 'prose-core'
import { Node as ProseNode } from 'prosemirror-model'
import { EditorState, Transaction } from 'prosemirror-state'
import { ourUuid } from 'utils'
import { Command } from '../types'

type SortOrder = 'asc' | 'desc'

export const _sortColumn = (
  posOfTable: number,
  sortOrder: SortOrder,
  columnIdx: number,
  state: EditorState,
): Transaction => {
  const table = findTableFromPos(posOfTable + 1, state.tr.doc)
  if (table) {
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })
    if (tableMap.hasVerticalMergedCells()) throw new Error("Can't sort table with vertical merged cells")
    let headerRowCount = 0
    // get the content of the cells of the given column
    const cellList = tableMap.getPosListInColumn(columnIdx).map((pos) => state.doc.nodeAt(pos))
    if (cellList.some((cell) => !cell)) throw new Error('Table map corrupt')
    while (true) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (cellList[0]!.type === schema.nodes.table_cell) break
      cellList.shift()
      headerRowCount += 1
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const cellContentList = cellList.map((cell) => cell!.textContent)
    // get the intended sort order of rows based on the content of the cells
    let sortedRowIndices = sortWithIndices(cellContentList, sortOrder)
    // if the table has header rows, add them back to the list of cells to sort
    if (headerRowCount > 0) {
      sortedRowIndices = sortedRowIndices.map((rowIndex) => rowIndex + headerRowCount)
      const headerRowIndices = Array.from(Array(headerRowCount).keys())
      sortedRowIndices = headerRowIndices.concat(sortedRowIndices)
    }
    // create a new table with the rows sorted
    const sortedTable = recreateTableWithSortedRows(table.node, sortedRowIndices)
    // replace the old table with the new table
    const tr = state.tr.replaceWith(table.pos, table.pos + table.node.nodeSize, sortedTable)
    return tr
  } else throw new Error('Table not found at given pos')
}

export const sortColumn =
  (sortOrder: SortOrder): Command =>
  (state, dispatch) => {
    const { selection } = state
    if (selection instanceof CellSelection || isTableSelection(selection)) {
      const rect = selectedRect(state.selection as CellSelection)
      const table = findTableFromSel(state.selection)
      // if no table or more than one column selected, return false
      if (!table || rect.width > 1) return false
      // if table has merged cell(s), return false
      if (rect.tableMap.hasVerticalMergedCells()) return false
      const selectedColumn = rect.left

      const tr = _sortColumn(rect.tableMap.tableNodeWithPos.posBeforeTable, sortOrder, selectedColumn, state)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
    }
    return false
  }

export const sortColumnAsc = sortColumn('asc')
export const sortColumnDesc = sortColumn('desc')

const sortWithIndices = (toSort: string[], sortOrder: SortOrder) => {
  const indices = toSort.map((_, i) => i)
  indices.sort((a, b) => {
    if (sortOrder === 'asc') {
      return toSort[a].localeCompare(toSort[b], 'en', { numeric: true })
    } else {
      return toSort[b].localeCompare(toSort[a], 'en', { numeric: true })
    }
  })
  return indices
}

const recreateTableWithSortedRows = (table: ProseNode, sortedRowIndices: number[]) => {
  const sortedRows = sortedRowIndices.map((rowIndex) => table.child(rowIndex))
  const newTable = schema.nodes.table.create(
    table.attrs,
    sortedRows.map((row) => {
      const rowCells = row.content.content.map((cell) => {
        return cell.type.create(cell.attrs, cell.content)
      })
      return schema.nodes.table_row.create(row.attrs, rowCells)
    }),
  )
  return newTable
}
